import BasicSelect from "@/components/form/BasicSelect"
import BootstrapButton from "@/components/form/BootstrapButton"
import type { Id, Nullable } from "@/models"
import { useCoreDataStore } from "@/stores/coreDataStore"
import usePopups from "@/stores/popupsStore"
import { css } from "vite-css-in-js"
import { ref, watch } from "vue"
import { Alert, defineComponent, inferProps, refSetter, useOnInput, type ReactiveComponent } from "vue-utils"

interface Props {
	value: string
	setValue(remarks: string): void
	canEdit: boolean
	required?: boolean
}

const remarksStyles = css`
	padding: 0.5rem 1rem;
	border: thin solid rgba(0, 0, 0, 0.15);
	border-radius: 0.25rem;
	background: rgba(255, 255, 255, 0.8);
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;

	.remark-input {
		border: thin solid rgba(0, 0, 0, 0.35);
		border-radius: 0.35rem;
		margin: 0.25rem 0;
		padding: 0.25rem 0.5em;
		height: 2.125em;
		width: 100%;
		max-width: 8rem;
		display: flex;
		align-items: center;
	}
`

const VtsLogRemarks: ReactiveComponent<Props> = (props) => {
	const coreDataStore = useCoreDataStore()
	const popups = usePopups()

	const freeTextTemplate = coreDataStore
		.data()
		.vtsRemarkTemplates.find((template) => template.name.toUpperCase() === "{{TEXT}}")
	const remarksTemplateRef = ref<string>(freeTextTemplate?.name ?? "")
	const editingRemarksTemplateRef = ref<Nullable<Id>>(null)

	const remarksWrapperRef = ref<HTMLDivElement>()
	const remarksRef = ref(props.value)

	watch(remarksRef, () => {
		props.setValue(remarksRef.value)
	})

	async function handleChangeTemplateClick() {
		editingRemarksTemplateRef.value = null
		const confirmation = await popups.confirm({
			confirmText: "Change",
			content: () => (
				<Alert title="Select Template">
					Select the template you would like to use.
					<br />
					<strong style={{ fontWeight: "600" }}>Please Note:</strong> This will clear your current remarks.
					<hr />
					<BasicSelect
						options={coreDataStore.data().vtsRemarkTemplates}
						value={editingRemarksTemplateRef.value}
						setValue={(template) => (editingRemarksTemplateRef.value = template.id)}
						defaultText="Select Template"
						disabled={!props.canEdit}
						style={{ width: "100%" }}
						required
					/>
				</Alert>
			),
		})
		if (confirmation) {
			remarksRef.value = ""

			if (editingRemarksTemplateRef.value !== null) {
				remarksTemplateRef.value =
					coreDataStore.lookupVtsRemarkTemplate(editingRemarksTemplateRef.value)?.name ?? freeTextTemplate?.name ?? ""
				setTimeout(() => handleInput(), 0)
			}
		}
	}

	function handleInput() {
		if (!remarksWrapperRef.value) return
		remarksRef.value = remarksWrapperRef.value.textContent ?? ""
	}

	const RenderTemplatedInput = () => {
		if (remarksTemplateRef.value === freeTextTemplate?.name) {
			return (
				<textarea
					value={remarksRef.value}
					onInput={useOnInput(refSetter(remarksRef))}
					placeholder={!props.canEdit ? "N/A" : "Enter remarks..."}
					rows={2}
					disabled={!props.canEdit}
					required={props.required}
				/>
			)
		}

		const templateParts = remarksTemplateRef.value.split(/({{TEXT}})/)

		return (
			<div class={remarksStyles} ref={remarksWrapperRef}>
				{templateParts.map((part, index) =>
					part === "{{TEXT}}" ? (
						<div class="remark-input" contenteditable key={index} onInput={() => handleInput()} />
					) : (
						<>{part}</>
					)
				)}
			</div>
		)
	}

	return () => (
		<>
			{remarksTemplateRef.value === "" ? (
				<BasicSelect
					options={coreDataStore.data().vtsRemarkTemplates}
					value={remarksTemplateRef.value}
					setValue={(template) => (remarksTemplateRef.value = template.name)}
					defaultText="Select Template"
					disabled={!props.canEdit}
					style={{ width: "100%" }}
					required
				/>
			) : (
				<>
					<RenderTemplatedInput />
					<small class="mt-2">
						Current Template: <code>{remarksTemplateRef.value}</code>
						{props.canEdit && (
							<BootstrapButton
								color="link"
								class="btn-sm py-0 ms-2"
								style="line-height: 1rem;"
								onClick={() => void handleChangeTemplateClick()}
							>
								Change Template
							</BootstrapButton>
						)}
					</small>
				</>
			)}
		</>
	)
}

export default defineComponent(VtsLogRemarks, inferProps<Props>())
